import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { SPARTACUS_UK_TIMEZONE } from 'src/constants/spartacus'
import { mapOpportunityTotals } from './crowsNestOpportunityTotals'

dayjs.extend(utc)
dayjs.extend(timezone)

export const CROWS_NEST_FEED_PAGE_SIZE = 25

export const CROWS_NEST_OPPORTUNITY_TYPE_LABELS = Object.freeze({
  paywall_limit: 'Paywall',
  supplier_added: 'Supplier add',
  supplier_milestone: 'Supplier milestone',
})

export const CROWS_NEST_FEED_TYPE_FILTERS = Object.freeze([
  { value: '', label: 'All types' },
  { value: 'paywall_limit', label: 'Paywall' },
  { value: 'supplier_added', label: 'Supplier add' },
  { value: 'supplier_milestone', label: 'Supplier milestone' },
])

/** Handoff filter — default empty = no status/owner filter. */
export const CROWS_NEST_FEED_HANDOFF_FILTERS = Object.freeze([
  { value: '', label: 'All leads' },
  { value: 'open', label: 'Open' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'mine', label: 'Mine' },
])

/** Match Growth Engine opportunity write cooldown (24h). */
export const CROWS_NEST_OPPORTUNITY_COOLDOWN_MS = 24 * 60 * 60 * 1000

/**
 * Map handoff dropdown → API query (status or lastTouchedByAccountId).
 * @param {string} handoffFilter
 * @param {string|null|undefined} accountId — current OMS admin for Mine
 */
export function buildOpportunityFeedHandoffQuery(handoffFilter, accountId) {
  if (handoffFilter === 'open' || handoffFilter === 'contacted') {
    return { status: handoffFilter }
  }
  if (handoffFilter === 'mine') {
    const id = accountId != null ? String(accountId).trim() : ''
    if (!id) return {}
    return { lastTouchedByAccountId: id }
  }
  return {}
}

export const CROWS_NEST_OPPORTUNITY_STATUS_LABELS = Object.freeze({
  open: 'Open',
  contacted: 'Contacted',
  qualified: 'Qualified',
  closed_won: 'Closed won',
  closed_lost: 'Closed lost',
  closed_duplicate: 'Duplicate',
})

export const CROWS_NEST_FEED_EDITABLE_STATUSES = Object.freeze([
  'open',
  'contacted',
])

export function formatOpportunityTypeLabel(type) {
  return CROWS_NEST_OPPORTUNITY_TYPE_LABELS[type] || type || '—'
}

export function formatOpportunityStatusLabel(status) {
  return CROWS_NEST_OPPORTUNITY_STATUS_LABELS[status] || status || 'Open'
}

export function formatOpportunityOccurredAt(value) {
  if (!value) return '—'
  const parsed = dayjs(value).tz(SPARTACUS_UK_TIMEZONE)
  if (!parsed.isValid()) return '—'
  return parsed.format('D MMM YYYY, HH:mm')
}

/**
 * Relative age for Crow’s Nest urgency (UK calendar day).
 * @param {string|Date|null|undefined} occurredAt
 * @param {import('dayjs').Dayjs|Date|string} [now]
 * @returns {{ label: string|null, urgency: 'today'|'recent'|'stale'|'none' }}
 */
export function formatOpportunityAgeBadge(occurredAt, now = dayjs()) {
  if (!occurredAt) {
    return { label: null, urgency: 'none' }
  }

  const parsed = dayjs(occurredAt).tz(SPARTACUS_UK_TIMEZONE)
  const nowUk = dayjs(now).tz(SPARTACUS_UK_TIMEZONE)
  if (!parsed.isValid() || !nowUk.isValid()) {
    return { label: null, urgency: 'none' }
  }

  const diffDays = nowUk.startOf('day').diff(parsed.startOf('day'), 'day')
  if (diffDays <= 0) {
    const hours = Math.max(0, nowUk.diff(parsed, 'hour'))
    if (hours < 1) {
      const minutes = Math.max(0, nowUk.diff(parsed, 'minute'))
      if (minutes < 1) return { label: 'Just now', urgency: 'today' }
      return { label: `${minutes}m ago`, urgency: 'today' }
    }
    return { label: hours === 1 ? '1h ago' : `${hours}h ago`, urgency: 'today' }
  }
  if (diffDays === 1) {
    return { label: 'Yesterday', urgency: 'recent' }
  }
  return {
    label: `${diffDays}d ago`,
    urgency: diffDays < 7 ? 'recent' : 'stale',
  }
}

/**
 * One-line “why this fired” from limit / supplier / metadata.
 * @param {object} item — API opportunity shape
 */
export function buildOpportunityWhyFired(item = {}) {
  if (!item || typeof item !== 'object') return null

  if (item.type === 'paywall_limit') {
    const parts = []
    const limit = item.limit || {}
    if (limit.currentUsage != null && limit.amount != null) {
      const unit = String(limit.unit || '').trim()
      parts.push(
        unit
          ? `${limit.currentUsage}/${limit.amount} ${unit}`
          : `${limit.currentUsage}/${limit.amount}`,
      )
    }

    const metadata = item.metadata || {}
    const attempt =
      metadata.attemptValue ||
      metadata.productTitle ||
      metadata.recipeTitle ||
      null
    if (attempt) {
      const label = String(metadata.attemptLabel || '').trim()
      parts.push(label ? `${label}: ${attempt}` : String(attempt))
    } else if (item.recentActivity?.summary) {
      parts.push(String(item.recentActivity.summary))
    }

    return parts.length ? parts.join(' · ') : null
  }

  if (item.type === 'supplier_added' || item.type === 'supplier_milestone') {
    const parts = []
    const supplier = item.supplier || {}
    if (supplier.supplierName) parts.push(String(supplier.supplierName))
    if (supplier.activeSupplierCountAfter != null) {
      parts.push(`${supplier.activeSupplierCountAfter} active after add`)
    }
    if (supplier.milestone != null) {
      parts.push(`Milestone ${supplier.milestone}`)
    }
    return parts.length ? parts.join(' · ') : null
  }

  const summary = item.recentActivity?.summary
  return summary ? String(summary) : null
}

/**
 * 24h dedupe hint for venue+feature (paywall / supplier opportunity writers).
 * @param {object} row — mapped feed row
 * @param {import('dayjs').Dayjs|Date|string} [now]
 * @returns {{ active: boolean, label: string }|null}
 */
export function buildOpportunityCooldownHint(row = {}, now = dayjs()) {
  if (!row?.occurredAt) return null
  if (
    !['paywall_limit', 'supplier_added', 'supplier_milestone'].includes(
      row.type,
    )
  ) {
    return null
  }

  const started = dayjs(row.occurredAt)
  const nowAt = dayjs(now)
  if (!started.isValid() || !nowAt.isValid()) return null

  const until = started.add(CROWS_NEST_OPPORTUNITY_COOLDOWN_MS, 'millisecond')
  const feature =
    row.featureLabel && row.featureLabel !== '—'
      ? row.featureLabel
      : 'This feature'

  if (nowAt.isBefore(until)) {
    return {
      active: true,
      label: `${feature} cooldown until ${formatOpportunityOccurredAt(
        until.toDate(),
      )} — same venue/feature won’t create another lead until then.`,
    }
  }

  return {
    active: false,
    label: `${feature} cooldown clear — same venue can signal again.`,
  }
}

export function formatOpportunityContactLabel(contact = {}) {
  const name = String(contact.name || '').trim()
  const email = String(contact.email || '').trim()
  const role = String(contact.role || '').trim()
  if (name && role) return `${name} (${role})`
  if (name) return name
  if (email) return email
  return '—'
}

export function getOpportunityMailtoHref(contact = {}) {
  const email = String(contact.email || '').trim()
  if (!email || !email.includes('@')) return null
  return `mailto:${email}`
}

/**
 * Prefill draft for pasting into an email client (mailto often cancels in-app).
 * @param {object} row — mapped feed row
 * @returns {{ to: string, subject: string, body: string }|null}
 */
export function buildOpportunityOutreachDraft(row = {}) {
  const email = String(row.contactEmail || '').trim()
  if (!email || !email.includes('@')) return null

  const venue = row.venueName && row.venueName !== '—' ? row.venueName : 'Venue'
  const signal = [row.typeLabel, row.featureLabel].filter(Boolean).join(' · ')
  const subject = `${venue} — ${signal || 'Crow’s Nest lead'}`

  const contactFirst =
    row.contactLabel && row.contactLabel !== '—'
      ? String(row.contactLabel).split(' (')[0].trim()
      : ''

  const body = [
    contactFirst ? `Hi ${contactFirst},` : 'Hi,',
    '',
    `Following up from Orders Made Simple about ${venue} (${
      row.plan || 'Free'
    }).`,
    signal ? `Signal: ${signal}` : null,
    row.whyFired ? `Context: ${row.whyFired}` : null,
    row.suggestedFollowUp && row.suggestedFollowUp !== '—'
      ? `Suggested follow-up: ${row.suggestedFollowUp}`
      : null,
    '',
    'Thanks,',
  ]
    .filter(line => line != null)
    .join('\n')

  return { to: email, subject, body }
}

/**
 * Plain-text outreach draft for clipboard → email provider paste.
 * @param {object} row — mapped feed row
 */
export function buildOpportunityOutreachCopyText(row = {}) {
  const draft = buildOpportunityOutreachDraft(row)
  if (!draft) return null
  return [`To: ${draft.to}`, `Subject: ${draft.subject}`, '', draft.body].join(
    '\n',
  )
}

/**
 * Mailto with subject/body (optional; prefer copy — browsers often cancel mailto in SPA).
 * @param {object} row — mapped feed row
 */
export function buildOpportunityOutreachMailto(row = {}) {
  const draft = buildOpportunityOutreachDraft(row)
  if (!draft) return null
  const params = [
    `subject=${encodeURIComponent(draft.subject)}`,
    `body=${encodeURIComponent(draft.body)}`,
  ].join('&')
  return `mailto:${draft.to}?${params}`
}

/**
 * Clipboard blob for Slack / HubSpot paste.
 * @param {object} row — mapped feed row
 */
export function buildOpportunityCopyText(row = {}) {
  const signal = [row.typeLabel, row.featureLabel].filter(Boolean).join(' · ')
  return [
    `Venue: ${row.venueName || '—'} (${row.plan || '—'})`,
    row.venueId ? `Venue ID: ${row.venueId}` : null,
    `Signal: ${signal || '—'}`,
    row.whyFired ? `Why: ${row.whyFired}` : null,
    `Contact: ${row.contactLabel || '—'}${
      row.contactEmail ? ` <${row.contactEmail}>` : ''
    }`,
    row.suggestedFollowUp && row.suggestedFollowUp !== '—'
      ? `Follow-up: ${row.suggestedFollowUp}`
      : null,
    row.occurredAtLabel ? `When: ${row.occurredAtLabel}` : null,
  ]
    .filter(Boolean)
    .join('\n')
}

export function formatSalesEmailLabel(salesEmail = {}) {
  if (salesEmail?.sentAt) {
    const when = formatOpportunityOccurredAt(salesEmail.sentAt)
    return when === '—' ? 'Sent' : `Sent ${when}`
  }
  if (salesEmail?.skippedReason) {
    return `Skipped (${salesEmail.skippedReason})`
  }
  return 'Not sent'
}

export function formatOpportunityFeatureLabel(item = {}) {
  if (item.featureName) return item.featureName
  if (item.featureId) return item.featureId
  if (item.type === 'supplier_added' || item.type === 'supplier_milestone') {
    const supplierName = item.supplier?.supplierName
    if (supplierName) return supplierName
    return 'Suppliers'
  }
  return '—'
}

/**
 * Normalize an API opportunity into a stable feed row.
 */
export function mapOpportunityFeedItem(item) {
  if (!item) return null
  const triggeredBy = item.triggeredBy || {}
  const lastTouchedBy = item.lastTouchedBy || {}
  const status = item.status || 'open'
  const age = formatOpportunityAgeBadge(item.occurredAt)
  const row = {
    key: item.id || `${item.venue?.id}-${item.occurredAt}`,
    id: item.id,
    type: item.type,
    typeLabel: formatOpportunityTypeLabel(item.type),
    status,
    statusLabel: formatOpportunityStatusLabel(status),
    occurredAt: item.occurredAt,
    occurredAtLabel: formatOpportunityOccurredAt(item.occurredAt),
    ageLabel: age.label,
    ageUrgency: age.urgency,
    whyFired: buildOpportunityWhyFired(item),
    venueId: item.venue?.id || null,
    venueName: item.venue?.name || '—',
    plan: item.plan || '—',
    featureLabel: formatOpportunityFeatureLabel(item),
    contactLabel: formatOpportunityContactLabel(triggeredBy),
    contactEmail: triggeredBy.email || '',
    contactMailto: getOpportunityMailtoHref(triggeredBy),
    contactPhone: triggeredBy.phone || '',
    suggestedFollowUp: item.suggestedFollowUp || '—',
    salesEmailLabel: formatSalesEmailLabel(item.salesEmail),
    salesEmailSent: Boolean(item.salesEmail?.sentAt),
    salesNote: item.salesNote || '',
    lastTouchedAt: item.lastTouchedAt || null,
    lastTouchedAtLabel: formatOpportunityOccurredAt(item.lastTouchedAt),
    lastTouchedByLabel: formatOpportunityContactLabel(lastTouchedBy),
    lastTouchedByEmail: lastTouchedBy.email || '',
  }
  row.outreachMailto = buildOpportunityOutreachMailto(row)
  row.outreachCopyText = buildOpportunityOutreachCopyText(row)
  row.copyText = buildOpportunityCopyText(row)
  const cooldown = buildOpportunityCooldownHint(row)
  row.cooldownHint = cooldown?.label || null
  row.cooldownActive = Boolean(cooldown?.active)
  row.lastTouchedByAccountId =
    lastTouchedBy.accountId != null ? String(lastTouchedBy.accountId) : null
  return row
}

export function mapOpportunityFeedResponse(response = {}) {
  const opportunities = Array.isArray(response.opportunities)
    ? response.opportunities.map(mapOpportunityFeedItem).filter(Boolean)
    : []
  return {
    opportunities,
    totals: mapOpportunityTotals(response.totals),
    pagination: response.pagination || {
      page: 1,
      pageSize: CROWS_NEST_FEED_PAGE_SIZE,
      total: 0,
      totalPages: 0,
      hasMore: false,
    },
  }
}
